import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Moment } from 'moment';
import { FilterChipOption } from 'shared/components/table-filter-chips/multi-value-filter-chip/models/filter-chip-option';
import { DeviceType } from 'shared/models/device-type';
import KeyValuePair from 'shared/models/key-value-pair';

import { ConnectorType } from 'features/connectors/domain/connector';
import { ReadFaultsHistoryFiltersResponse } from 'features/faults-history/domain/models/read-faults-history-filters-response';
import { useLazyReadFaultsHistoryFilters } from 'features/faults-history/domain/queries/faults-history-queries';
import { GatewayType } from 'features/gateway/domain/models/gateway-type';
import { SystemType } from 'features/systems/domain/models/system';

export interface FaultsHistoryAvailableFiltersHook {
  readFaultsHistoryFilterTypes: ReadFaultsHistoryFiltersResponse | undefined;
  readFaultsHistoryIsLoading: boolean;

  categoryOptions: FilterChipOption[];

  deviceFaultTypeOptions: FilterChipOption[];
  gatewayFaultTypeOptions: FilterChipOption[];
  systemFaultTypeOptions: FilterChipOption[];
  connectorFaultTypeOptions: FilterChipOption[];

  deviceTypeOptions: FilterChipOption[];
  gatewayTypeOptions: FilterChipOption[];
  systemTypeOptions: FilterChipOption[];
  connectorTypeOptions: FilterChipOption[];

  statusOptions: KeyValuePair[];
}

const useFaultsHistoryAvailableFiltersHook = (dateFrom: Moment | null, dateTo: Moment | null): FaultsHistoryAvailableFiltersHook => {
  const { t } = useTranslation('faultsHistory');

  const devicePrefix = 'device-';
  const systemPrefix = 'system-';
  const gatewayPrefix = 'gateway-';
  const connectorPrefix = 'connector-';

  const {
    refetch: triggerReadFaultsHistoryFilterTypes,
    data: readFaultsHistoryFilterTypes,
    isPending: readFaultsHistoryIsLoading,
  } = useLazyReadFaultsHistoryFilters({
    dateFrom,
    dateTo,
  });

  useEffect(() => {
    triggerReadFaultsHistoryFilterTypes();
  }, [dateFrom, dateTo]);

  const categoryOptions = (readFaultsHistoryFilterTypes?.categoryTypeFilterOptions ?? []).map((value) => ({
    key: value,
    value: [value],
    label: t(`category.${value}`),
  }));

  const deviceFaultTypeOptions = (readFaultsHistoryFilterTypes?.deviceFaultTypeFilterOptions ?? []).map((value) => ({
    key: value,
    value: [value],
    label: t(`faultType.${value}`),
  }));

  const gatewayFaultTypeOptions = (readFaultsHistoryFilterTypes?.gatewayFaultTypeFilterOptions ?? []).map((value) => ({
    key: value,
    value: [value],
    label: t(`faultType.${value}`),
  }));

  const systemFaultTypeOptions = (readFaultsHistoryFilterTypes?.systemFaultTypeFilterOptions ?? []).map((value) => ({
    key: value,
    value: [value],
    label: t(`faultType.${value}`),
  }));

  const connectorFaultTypeOptions = (readFaultsHistoryFilterTypes?.connectorFaultTypeFilterOptions ?? []).map((value) => ({
    key: value,
    value: [value],
    label: t(`faultType.${value}`),
  }));

  const deviceTypeOptions = (readFaultsHistoryFilterTypes?.deviceTypeFilterOptions ?? [])
    .filter((value) => value !== DeviceType.Unknown)
    .map((value) => ({
      key: devicePrefix + value,
      value: [devicePrefix + value],
      label: t(`deviceType.${value}`),
    }));

  const gatewayTypeOptions = (readFaultsHistoryFilterTypes?.gatewayTypeFilterOptions ?? [])
    .filter((value) => value !== GatewayType.Unknown)
    .map((value) => ({
      key: gatewayPrefix + value,
      value: [gatewayPrefix + value],
      label: t(`gatewayType.${value}`),
    }));

  const systemTypeOptions = (readFaultsHistoryFilterTypes?.systemTypeFilterOptions ?? [])
    .filter((value) => value[1] !== SystemType.Unknown)
    .map((value) => ({
      key: systemPrefix + value,
      value: [systemPrefix + value],
      label: t(`systemType.${value}`),
    }));

  const connectorTypeOptions = (readFaultsHistoryFilterTypes?.connectorTypeFilterOptions ?? [])
    .filter((value) => value[1] !== ConnectorType.Unknown)
    .map((value) => ({
      key: connectorPrefix + value,
      value: [connectorPrefix + value],
      label: t(`connectorType.${value}`),
    }));

  const statusOptions = [
    { key: 'Open', value: t('status.Open') },
    { key: 'Resolved', value: t('status.Resolved') },
  ];

  return {
    readFaultsHistoryFilterTypes,
    readFaultsHistoryIsLoading,
    categoryOptions,
    deviceTypeOptions,
    gatewayTypeOptions,
    systemTypeOptions,
    connectorTypeOptions,
    deviceFaultTypeOptions,
    gatewayFaultTypeOptions,
    systemFaultTypeOptions,
    connectorFaultTypeOptions,
    statusOptions,
  };
};

export default useFaultsHistoryAvailableFiltersHook;
